<template>
  <div>
    <GpBtn
      class="tw-w-auto xl:tw-hidden"
      btn-type="transparent"
      size="sm"
      @click="enableMobileNavigation"
    >
      <FontAwesomeIcon icon="bars" />
    </GpBtn>
    <teleport to="#app">
      <GpNavigation v-model:is-opened="isMobileMenuToggled">
        <template #nav-body>
          <router-link :to="{ name: 'Home' }" class="tw-none tw-max-w-40 sm:tw-block">
            <Logo width="100%" />
          </router-link>

          <ProjectSwitcher class="tw-mt-8 tw-w-full" />

          <template v-if="hasActiveProject">
            <GpNavigationGroup class="tw-mt-4">
              <template #nav-group-links>
                <SidebarRouterLink
                  v-if="isProjectOwner"
                  :to="{
                    name: 'ProjectSettings',
                    params: { projectId: currentProjectId },
                  }"
                  :is-active="route.name === 'ProjectSettings'"
                  :icon="['fas', 'wrench']"
                  label="Settings"
                />
                <SidebarRouterLink
                  :to="{
                    name: 'ProjectMembers',
                    params: { projectId: currentProjectId },
                  }"
                  :is-active="route.name === 'ProjectMembers'"
                  :icon="['fas', 'user']"
                  label="Members"
                />
                <SidebarRouterLink
                  v-if="sessionStore.hasTag(UserTag.ACCESS_TO_CLOUD_PROVIDERS)"
                  :to="{
                    name: 'ProjectCloudProviders',
                    params: { projectId: currentProjectId },
                  }"
                  :is-active="route.name === 'ProjectCloudProviders'"
                  v-slot="{ href, navigate, isActive, isLinkActive }"
                >
                  <GpNavigationItemLink
                    class="tw-text-gp-mouse hover:tw-text-gp-mouse-light"
                    :href="href"
                    :is-active="isLinkActive || isActive"
                    @click="navigate"
                  >
                    <FontAwesomeIcon :icon="['fas', 'cloud']" class="tw-mr-1 tw-text-gp-rhino" />
                    Cloud providers
                    <GpTag tag-type="warning" label="BETA" size="xs" class="tw-ml-2" outline />
                  </GpNavigationItemLink>
                </SidebarRouterLink>
                <SidebarRouterLink
                  v-if="isProjectOwner"
                  :to="{
                    name: 'ProjectAuditLog',
                    params: { projectId: currentProjectId },
                  }"
                  :is-active="route.name === 'ProjectAuditLog'"
                  :icon="['fas', 'search']"
                  label="Audit log"
                />
              </template>
            </GpNavigationGroup>
            <GpNavigationGroup class="tw-mt-8">
              <template #nav-group-header> Resources </template>
              <template #nav-group-links>
                <GpNavigationItem>
                  <SidebarRouterLink
                    :to="{ name: 'ProjectNodes', params: { projectId: currentProjectId } }"
                    :is-active="route.name === 'ProjectNodes'"
                    :icon="['fas', 'server']"
                    label="Nodes"
                  />
                  <SidebarRouterLink
                    :to="{
                      name: 'ProjectImages',
                      params: { projectId: currentProjectId },
                    }"
                    :is-active="route.name === 'ProjectImages'"
                    :icon="['fas', 'fa-compact-disc']"
                    label="Images"
                  />
                </GpNavigationItem>
              </template>
            </GpNavigationGroup>
          </template>
          <GpNavigationGroup class="tw-mt-4">
            <template #nav-group-header> Help </template>
            <template #nav-group-links>
              <SidebarStaticLink
                :href="$config.docs.url"
                :icon="['fas', 'book']"
                label="Documentation"
                target="_blank"
              />
              <SidebarStaticLink
                :href="$config.support.url"
                :icon="['fas', 'headset']"
                label="Support"
                target="_blank"
              />
              <SidebarStaticLink
                href="https://status.gpcore.io/"
                :icon="['fas', 'signal-bars']"
                label="Status"
                target="_blank"
              />
              <SidebarRouterLink
                v-if="showDebug"
                :to="{ name: 'Debug' }"
                :icon="['fas', 'bug']"
                label="Debug"
              />
            </template>
          </GpNavigationGroup>
        </template>
      </GpNavigation>
    </teleport>
  </div>
</template>

<script lang="ts">
import Logo from 'gpcore-uikit/icons/logo.vue';
import SidebarRouterLink from 'gpcore-uikit/components/new/sidebar/SidebarRouterLink.vue';
import SidebarStaticLink from 'gpcore-uikit/components/new/sidebar/SidebarStaticLink.vue';
import { UserTag } from 'gpcore-grpc/gpcore/api/cloud/v1/user_pb';
import { computed, defineComponent, ref } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  GpBtn,
  GpNavigation,
  GpNavigationGroup,
  GpNavigationItem,
  GpNavigationItemLink,
  GpTag,
} from '@gportal/uikit';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import { useSessionStore } from 'gpcore-apikit/store/pinia/session';
import { useRoute, useRouter } from 'vue-router';
import { ProjectMemberRole } from 'gpcore-grpc/gpcore/api/cloud/v1/project_pb';
import ProjectSwitcher from './ProjectSwitcher.vue';

export default defineComponent({
  computed: {
    ProjectMemberRole() {
      return ProjectMemberRole;
    },
  },
  components: {
    Logo,
    FontAwesomeIcon,
    GpNavigation,
    GpNavigationGroup,
    GpNavigationItem,
    GpNavigationItemLink,
    GpTag,
    GpBtn,
    ProjectSwitcher,
    SidebarRouterLink,
    SidebarStaticLink,
  },
  setup() {
    const isMobileMenuToggled = ref<boolean>(false);
    const enableMobileNavigation = () => {
      isMobileMenuToggled.value = true;
    };
    const router = useRouter();
    router.afterEach(() => {
      if (isMobileMenuToggled.value) isMobileMenuToggled.value = false;
    });
    const route = useRoute();

    const projectStore = useProjectStore();
    const currentProjectId = computed(() => projectStore.projectId);
    const project = computed(() => projectStore.project);
    const hasActiveProject = computed(
      () =>
        project.value != null &&
        currentProjectId.value != null &&
        currentProjectId.value.length > 0,
    );
    const membership = computed(() => projectStore.currentUserMembership);
    const isProjectOwner = computed(
      () => membership.value != null && membership.value.role === ProjectMemberRole.OWNER,
    );

    const sessionStore = useSessionStore();
    const showDebug = computed(() => sessionStore.hasRole('super-admin'));

    return {
      UserTag,
      showDebug,
      hasActiveProject,
      currentProjectId,
      isProjectOwner,
      route,
      isMobileMenuToggled,
      enableMobileNavigation,
      sessionStore,
    };
  },
});
</script>
